import React, { useContext } from "react";
import { DoubleColumnWrapper, SecondColumn, SingleProductContentWrapper } from "./Styled";
import { ProductOrder } from "../../../data/entities";
import ProductList from "./ProductList";
import CustomerInfo from "./CustomerInfo";
import Priority from "./Priority";
import Note from "./Note";
import Summary from "./Summary";
import { Assignees } from "./Assignees";
import apiMethods from "../../../utils/api";
import { PRODUCT_CATEGORY, ROLE, SALE_ORDER_STATUS } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import Upload from "./Upload";
import { MaterialRequestLink } from "./MaterialRequestLink";
import PurchaseOrderNumber from "./PurchaseOrderNumber";

interface SingleProductOrderProps {
    productOrder: ProductOrder & { order_name?: string; notes?: string };
    mergeProductOrder: Function;
    fetchProductOrder?: any;
    isView?: boolean;
    disableItemEditing?: boolean;
    disableHeaderEditing?: boolean;
    showPoField?: boolean;
    onAssignmentsUpdated?: () => void;
}

interface AssigneeField {
    title: string; // The display title for the assignee
    fieldName: string; // The field name used for the assignee
    role: string; // The role of the assignee
}

const SingleProductOrder = ({
                                productOrder,
                                mergeProductOrder,
                                fetchProductOrder,
                                isView = false,
                                disableItemEditing = false,
                                disableHeaderEditing = false,
                                showPoField = true,
                                onAssignmentsUpdated,
                            }: SingleProductOrderProps) => {
    const updateProductOrder = async (data: any) => {
        await apiMethods.patch(`/products/orders/${productOrder?.id}/`, data);
    };

    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSeeSummary = ![
        ROLE.FACTORY_MANAGER_ONE,
        ROLE.FACTORY_MANAGER_TWO,
        ROLE.DEPUTY_FACTORY_MANAGER_ONE,
        ROLE.DEPUTY_FACTORY_MANAGER_TWO,
        ROLE.FIRST_STORAGE_KEEPER,
    ].includes(role);

    const inferRequiredStorageKeepers = (po: any): string[] => {
        const keepers = new Set<string>();
        const items = Array.isArray(po?.items) ? po.items : [];

        items.forEach((it: any) => {
            const pStorage = it?.product?.storage || "";
            const mStorage = it?.material?.storage || "";
            const s1 = String(pStorage || mStorage).toLowerCase();
            if (/kho\s*1/i.test(pStorage) || /kho\s*1/i.test(mStorage)) {
                keepers.add("first_storage_keeper");
            }
            if (/kho\s*2/i.test(pStorage) || /kho\s*2/i.test(mStorage)) {
                keepers.add("second_storage_keeper");
            }
        });

        if (keepers.size === 0 && items.length > 0) {
            keepers.add("first_storage_keeper");
        }
        return Array.from(keepers);
    };

    const assigneeCases = (): AssigneeField[] => {
        const requiredStorageKeepers = inferRequiredStorageKeepers(productOrder);

        const storageKeeperAssignments = requiredStorageKeepers
            .map((keeper) => {
                if (keeper === "first_storage_keeper") {
                    return {
                        title: "Thủ kho 1",
                        fieldName: "first_storage_keeper",
                        role: "first_storage_keeper",
                    };
                } else if (keeper === "second_storage_keeper") {
                    return {
                        title: "Thủ kho 2",
                        fieldName: "second_storage_keeper",
                        role: "second_storage_keeper",
                    };
                }
                return null;
            })
            .filter((assignment): assignment is AssigneeField => assignment !== null);

        const hasBarrel = (productOrder.products || []).some((product: any) =>
            String(product?.category || "").split(";").some((category) =>
                [PRODUCT_CATEGORY.BARREL, PRODUCT_CATEGORY.VESSEL].includes(category.trim() as PRODUCT_CATEGORY),
            ),
        );
        // Saved barrel assignments remain relevant even before manufacturing or
        // when stock covers the order. Keep the existing automotive conditions.
        const barrelAssignments: AssigneeField[] = hasBarrel ? [{
            title: "Xưởng Phuy Thùng",
            fieldName: "factory_manager_one",
            role: "factory_manager_one",
        }] : [];

        {
            const saleStatus = String(productOrder.status).toUpperCase();
            const showFactoryManagerInStatuses = new Set([
                "LOGISTIC_REVIEWED",
                "MATERIAL_REQUESTED_AND_MANUFACTURING",
                "IMPORTING_AND_EXPORTING",
                "COMPLETED",
            ]);
            if (showFactoryManagerInStatuses.has(saleStatus)) {
                const products = productOrder.products || [];
                const hasVehicle = products.some((p: any) => {
                    const cat = (p?.category || "").toString().toLowerCase();
                    return cat.includes("cơ khí ô tô") || cat.includes("oto") || cat.includes("ô tô") || cat === "vehicle";
                });

                // Determine if manufacturing is needed based on requirements shape
                const requirements: any =
                    (productOrder as any)?.requirement ||
                    (productOrder as any)?.requirements ||
                    {};
                const missingProducts = Array.isArray(requirements?.missing_products)
                    ? requirements.missing_products
                    : [];
                // Backward compatibility: some payloads expose a single number `missing_quantity`
                const legacyMissingQty = Number((requirements as any)?.missing_quantity ?? 0);
                const totalMissing = (missingProducts || []).reduce((sum: number, it: any) => {
                    const n = Number(it?.missing_qty ?? it?.missing_quantity ?? it?.required_qty ?? 0);
                    return sum + (isNaN(n) ? 0 : n);
                }, 0) + (isNaN(legacyMissingQty) ? 0 : legacyMissingQty);
                const needsManufacturing = totalMissing > 0;

                if (!needsManufacturing) {
                    return [...barrelAssignments, ...storageKeeperAssignments];
                }

                const role = hasVehicle ? "factory_manager_two" : "factory_manager_one";
                const title = hasVehicle ? "Xưởng Cơ Khí Ô Tô" : "Xưởng Phuy Thùng";
                const items: AssigneeField[] = [
                    ...(hasVehicle ? barrelAssignments : []),
                    {
                        title,
                        fieldName: "factory_manager",
                        role,
                    },
                ];
                if (
                    saleStatus === "MATERIAL_REQUESTED_AND_MANUFACTURING" ||
                    saleStatus === "IMPORTING_AND_EXPORTING"
                ) {
                    items.push(...storageKeeperAssignments);
                }
                return items;
            }
        }

        const saleStatus = String(productOrder.status).toUpperCase();
        switch (saleStatus) {
            case SALE_ORDER_STATUS.OPEN:
                return barrelAssignments;
            case SALE_ORDER_STATUS.LOGISTIC_REVIEWED:
                return [...storageKeeperAssignments];
            case SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING:
                return [...storageKeeperAssignments];
            case SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING:
                return [...storageKeeperAssignments];
            case SALE_ORDER_STATUS.COMPLETED:
                return [...storageKeeperAssignments];
            case SALE_ORDER_STATUS.CANCELLED:
                return [];
            default:
                return [...storageKeeperAssignments];
        }
    };

    const defaultAssignees = () => {
        const defaultAssignees = [
            {
                title: "Nhân viên Sale",
                fieldName: "sale_staff",
                role: "sale_staff",
            },
            {
                title: "Nhân viên hậu cần điều phối",
                fieldName: "production_staff",
                role: "production_staff",
                assignmentRoles: ["production_staff", "logistic_manager"],
            },
        ];
        return defaultAssignees;
    };

    return (
        <SingleProductContentWrapper>
            {/* Material order links removed for SaleOrder */}
            {productOrder.material_requests && productOrder.material_requests.length > 0 && (
                <MaterialRequestLink
                    materialRequests={
                        productOrder.material_requests || []
                    }
                />
            )}
            <ProductList
                productOrder={productOrder}
                mergeProductOrder={mergeProductOrder}
                isView={isView || disableItemEditing}
                orderState={productOrder.status}
            />
            <DoubleColumnWrapper>
                <SecondColumn>
                    <CustomerInfo customer={productOrder.customer} deliveryAddress={productOrder.delivery_address} />
                    {canSeeSummary && (
                        <Summary
                            data={productOrder.products}
                            materials={productOrder.materials}
                            orderState={productOrder.status}
                        />
                    )}
                    <Upload
                        model="product_flow.SaleOrder"
                        objectId={productOrder.id}
                        objectName={productOrder.order_name || productOrder.name}
                        customerName={productOrder.customer?.name}
                        customerId={productOrder.customer?.id}
                    />
                </SecondColumn>
                <SecondColumn>
                    {showPoField && (
                        <PurchaseOrderNumber
                            value={productOrder.po_number}
                            setValue={(value) => mergeProductOrder({ po_number: value })}
                            isView={isView}
                        />
                    )}
                    {!isView && !disableHeaderEditing && (
                        <Priority
                            productOrder={productOrder}
                            mergeProductOrder={mergeProductOrder}
                        />
                    )}

                    {isView && (
                        <Assignees
                            data={productOrder}
                            updateData={updateProductOrder}
                            fetchData={fetchProductOrder}
                            fields={[...defaultAssignees(), ...assigneeCases()]}
                            onAssignmentsUpdated={onAssignmentsUpdated}
                            disabledEdit={disableItemEditing}
                        />
                    )}

                    <Note
                        text={productOrder.notes ?? productOrder.note ?? ""}
                        setText={(value: string) => {
                            mergeProductOrder({ notes: value });
                        }}
                        isView={isView}
                    />
                </SecondColumn>
            </DoubleColumnWrapper>
        </SingleProductContentWrapper>
    );
};

export default SingleProductOrder;
